import type {
    WixAppRuntimeEnvironment,
    WixImageResource,
    WixMetaSiteInfo,
    WixPremiumSubscriptionInfo,
    WixUserDomain
} from "@wix/devzai-common-wix";
import {assertWixImageResource} from "@wix/devzai-common-wix";
import type {ObjectFieldsAssertion, OmitStrict, Values} from "@wix/devzai-utils-common";
import {
    arrayFirst,
    assertNotNullable,
    evaluateFunction,
    iterableFindAndSelect,
    iterableMapToArray,
    objectGetValues,
    stringTrimPrefix,
    valueConvertWhenNotNullable
} from "@wix/devzai-utils-common";
import type {DoppeDtoUserSettings} from "./doppe-dto-user-settings";
import {doppeDtoUserSettingsExtendDefaultValues, doppeDtoUserSettingsGetSettingValue} from "./doppe-dto-user-settings";
import type {DoppeDtoBymoPage, DoppeDtoBymoPageOwnerData} from "./doppe-dto-bymo-page";
import type {BymoPageStatus} from "./bymo-page-status";
import {doppeValidateUserDisplayName, doppeValidateUserSubdomain} from "../doppe-validators/doppe-validators";
import type {DoppeThirdPartyApps} from "./doppe-third-party-apps";
import type {DoppeDtoUserOnboardingLeadData} from "./doppe-dto-user-onboarding-lead-data";
import type {DoppeOnboardingType} from "./doppe-onboarding-type";
import type {DoppeOnboardingPreferences} from "./doppe-onboarding-preferences";
import type {DoppeBlockingStatus} from "./doppe-blocking-reason";
import {doppeDtoAppFeatureGetValue} from "./doppe-dto-app-features";
import {
    doppeFreemiumPlanIsEnabled,
    doppeFreemiumPlanIsMultiplePackagesPremiumModel,
    doppeFreemiumPlanIsSinglePackagePremiumModel
} from "../doppe-premium";
import {DoppeContactsMetaSiteAutoValues} from "./doppe-contacts-meta-site";
import type {DoppeDtoUserOnboardingData} from "./doppe-dto-user-onboarding-data";
import type {DoppeDtoWixUserAppSettings} from "./doppe-dto-wix-user";
import {DoppeImportedAccountSubdomainPrefix} from "../constants/doppe-constants";
import type {DoppeAppVariant} from "./doppe-app-variant";
import type {DoppeHtmlSiteUriMappings, DoppeHtmlSiteUriMappingType} from "./doppe-html-site";

/**
 * Types of MetaSites available in a Hopp project.
 * 
 * MetaSites are Wix's foundational containers that provide infrastructure for
 * app installations, domain connections, premium subscriptions, and site management.
 * Used as discriminators to determine which MetaSite ID to retrieve from a Hopp project.
 */
export const DoppeDtoUserMetaSiteType = {
    /** 
     * The primary MetaSite created for every Hopp project.
     * Created when user starts onboarding and always exists for active projects.
     */
    CreatorsMetaSite: 'CreatorsMetaSite',
    
    /** 
     * Optional existing Wix site linked to showcase data in Hopp project.
     * Can be null if no site is linked.
     */
    LinkedMetaSite: 'LinkedMetaSite',
} as const;

export type DoppeDtoUserMetaSiteType = Values<typeof DoppeDtoUserMetaSiteType>;

/**
 * ⚠️ NAMING INCONSISTENCY WARNING:
 * DoppeDtoUser represents a HOPP PROJECT, not a person/identity.
 * 
 * This is a legacy naming issue where "User" actually means "Project".
 * A Hopp project contains multiple assets: short links, link-in-bio pages, instant pages.
 * The actual person/identity is represented by wixUserId (the signed-in user).
 * 
 * Architecture:
 * - One Wix User (person) ←→ Many DoppeDtoUsers (Hopp projects)
 * - One DoppeDtoUser (Hopp project) ←→ Many Wix Users (collaborators)
 */
export interface DoppeDtoUser extends DoppeDtoUser.OwnerWixUserData {
    /** Project ID - NOT a person ID */
    id: string;
    /** Project subdomain (e.g., "myproject" in myproject.hopp.bio) */
    subdomain: string;
    /** The Wix User ID of the project owner (actual person ID) */
    wixUserId: string | null;
    /** Project display name - NOT the person's name */
    displayName: string;
    /** 
     * Main MetaSite ID (UUID) - contains Linked MetaSite ID if exists, otherwise Creators MetaSite ID
     * @deprecated ⚠️ Never access directly in business logic - use doppeDtoUserGetMainMetaSiteId()
     */
    wixMetaSiteId: string | null;
    /** 
     * Creators MetaSite ID (UUID) - the primary MetaSite created for this Hopp project
     * @deprecated ⚠️ Never access directly in business logic - use doppeDtoUserGetCreatorsMetaSiteId()
     */
    creatorsWixMetaSiteId: string | null;
    profilePicture: WixImageResource | null;
    userSettings: DoppeDtoUserSettings;
    bymoPageId: DoppeDtoBymoPage.Id;
    bymoPageStatus: BymoPageStatus;
    speedDialPageId?: DoppeDtoBymoPage.Id;
    connectedTpas: DoppeThirdPartyApps.IntegrationData[];
    /** 
     * Indicates if the project is currently in onboarding process.
     * 
     * - `null`: No ongoing onboarding (could be Inactive, or ActiveAndCompletedOnboarding)
     * - `DoppeOnboardingType`: Currently onboarding (ActiveDuringOnboarding state)
     * 
     * Note: To determine if onboarding is completed, check both this field AND if user is active.
     */
    ongoingOnboardingType: DoppeOnboardingType | null;
    appVariant: DoppeAppVariant;

    leadData: DoppeDtoUserOnboardingLeadData;
    latestLeadData: DoppeDtoUserOnboardingLeadData;
    onboardingPreferences: DoppeOnboardingPreferences | null;
}

export namespace DoppeDtoUser {

    export type CommonData = DoppeDtoBymoPageOwnerData;

    export interface OwnerWixUserData {
        ownerWixUserAppSettings: DoppeDtoWixUserAppSettings;
        isBlocked: DoppeBlockingStatus;
    }

    export type Id = DoppeDtoUser['id'];

    export type Active = OmitStrict<DoppeDtoUser, 'wixUserId' | 'wixMetaSiteId' | 'creatorsWixMetaSiteId'> & {
        wixUserId: Exclude<DoppeDtoUser['wixUserId'], null>;
        wixMetaSiteId: Exclude<DoppeDtoUser['wixMetaSiteId'], null>;
        creatorsWixMetaSiteId: Exclude<DoppeDtoUser['creatorsWixMetaSiteId'], null>;
    }

    export type ActiveAndCompletedOnboarding = OmitStrict<Active, 'ongoingOnboardingType'> & {
        ongoingOnboardingType: null;
    }

    export type ActiveDuringOnboarding = OmitStrict<Active, 'ongoingOnboardingType'> & {
        ongoingOnboardingType: DoppeOnboardingType;
    }

    export type Inactive = OmitStrict<DoppeDtoUser, 'wixUserId' | 'wixMetaSiteId' | 'creatorsWixMetaSiteId' | 'isBlocked'> & {
        wixUserId: null;
        wixMetaSiteId: null;
        creatorsWixMetaSiteId: null;
        isBlocked: false;
    }

    export type ForCreation = Pick<DoppeDtoUser, 'subdomain' | 'displayName' | 'profilePicture'>;

    export type ForAdminCreation = Pick<DoppeDtoUser, 'subdomain' | 'displayName' | 'profilePicture' | 'userSettings' | 'speedDialPageId'> & {
        adminNotes: string;
        boStarred: boolean;
    };

    export type ForUpdate = ForCreation;

    export type ForAdminUpdate = ForUpdate & {
        appVariant: DoppeAppVariant;
    };

    export type WithAdminData = DoppeDtoUser & {
        isBaseUser: boolean;
        isCurrentUser: boolean;
        createdDate: number;
        hasLinkWithHoppPage: boolean;
        linksCount: number | null;
        connectedLinksCount: number | null;
        pagesCount: number | null;
        landingPagesCount: number | null;
        preRollsCount: number | null;
        lastVisitDate: number | null;
        adminNotes: string;
        boStarred: boolean;
        originalWixUserId: string | null;
        originalWixMetaSiteId: string | null;
        biGroups: string[];
        invitationCode: string | null;
        onboardingData: DoppeDtoUserOnboardingData | null;
        isPartner: boolean;
        usedAiOnboardingPageGeneration: boolean;

        linkInBioPage: DoppeDtoBymoPage | null;
        userEmail: string | null;
        subscriptionInfo: WixPremiumSubscriptionInfo | null;
        linkedSiteSubscriptionInfo: WixPremiumSubscriptionInfo | null;
        creatorsMetaSiteInfo: WixMetaSiteInfo.WithInstalledTpasMetadata<WixMetaSiteInfo.FullInfo> | null;
        linkedMetaSiteInfo: WixMetaSiteInfo.WithInstalledTpasMetadata<WixMetaSiteInfo.FullInfo> | null;
        connectedDomain: WixUserDomain | null;
        wixUserParentAccountId: string | null;
        premiumWixSitesCount: number | null;
        sourceEnvironment: WixAppRuntimeEnvironment | null;
    }

    export type ExternalAdminDataFields = keyof Pick<WithAdminData,
        | 'linkInBioPage' | 'userEmail' | 'subscriptionInfo'
        | 'connectedDomain' | 'wixUserParentAccountId' | 'premiumWixSitesCount' | 'linkedSiteSubscriptionInfo'
        | 'creatorsMetaSiteInfo' | 'linkedMetaSiteInfo'
    >

    export type ImpersonationData = Pick<DoppeDtoUser, 'id' | 'displayName' | 'subdomain'>

    export type WithFreemiumPlanData = {
        userSettings: Pick<DoppeDtoUser['userSettings'], 'freemiumPlan'>
    }
}

export const DoppeDtoUserForCreationFieldsAssertions: ObjectFieldsAssertion<DoppeDtoUser.ForCreation> = {
    displayName: assert => assert.isString(doppeValidateUserDisplayName),
    subdomain: assert => assert.isString(doppeValidateUserSubdomain),
    profilePicture: assert => assert.isUnion(
        assert => assert.isNull(),
        assert => assert.usingAssertionFunction(assertWixImageResource)
    )
}

export function doppeDtoUserHasCompletedOnboarding (doppeUser: Pick<DoppeDtoUser, 'wixUserId' | 'ongoingOnboardingType'>) : doppeUser is DoppeDtoUser.ActiveAndCompletedOnboarding {
    return doppeUser.wixUserId !== null && doppeUser.ongoingOnboardingType === null;
}

export function doppeDtoUserIsActiveDuringOnboarding (doppeUser: Pick<DoppeDtoUser, 'wixUserId' | 'ongoingOnboardingType'>) : doppeUser is DoppeDtoUser.ActiveDuringOnboarding {
    return doppeUser.wixUserId !== null && doppeUser.ongoingOnboardingType !== null;
}

export function doppeDtoUserIsActive (doppeUser: DoppeDtoUser) : doppeUser is DoppeDtoUser.Active {
    return doppeUser.wixUserId !== null;
}

export function doppeDtoUserGetProjectName (doppeUser: Pick<DoppeDtoUser, 'displayName'>) {
    return doppeUser.displayName;
}

export function doppeDtoUserGetSourceEnvironmentWhenImported (doppeUser: DoppeDtoUser) : WixAppRuntimeEnvironment | null {
    const accountName = doppeUser.subdomain;

    return iterableFindAndSelect(Object.entries(DoppeImportedAccountSubdomainPrefix), ([env, prefix], skip) => {
        return accountName.startsWith(prefix) ? env as WixAppRuntimeEnvironment : skip;
    }) ?? null;
}

/**
 * Gets the Main MetaSite ID for a Hopp project.
 * 
 * Returns the Linked MetaSite ID if the user has linked an existing Wix site,
 * otherwise returns the Creators MetaSite ID.
 * 
 * @param doppeUser - The Hopp project data
 * @returns Main MetaSite ID (UUID) - never null for active projects
 */
export function doppeDtoUserGetMainMetaSiteId<T extends Pick<DoppeDtoUser, 'wixMetaSiteId' | 'creatorsWixMetaSiteId'>> (
    doppeUser: T
) : T['creatorsWixMetaSiteId'] {
    return doppeDtoUserGetLinkedMetaSiteId(doppeUser) ?? doppeDtoUserGetCreatorsMetaSiteId(doppeUser);
}

/**
 * Gets the Creators MetaSite ID for a Hopp project.
 * 
 * The Creators MetaSite is the primary MetaSite created when a user starts onboarding.
 * It provides Wix infrastructure (domains, apps, subscriptions) for the Hopp project.
 * 
 * @param doppeUser - The Hopp project data
 * @returns Creators MetaSite ID (UUID) or null for inactive projects
 */
export function doppeDtoUserGetCreatorsMetaSiteId<T extends Pick<DoppeDtoUser, 'creatorsWixMetaSiteId'>> (
    doppeUser: T
) : T['creatorsWixMetaSiteId'] {
    return doppeUser.creatorsWixMetaSiteId;
}

export function doppeDtoUserGetMetaSiteIdForDuplexer<T extends Pick<DoppeDtoUser, 'creatorsWixMetaSiteId'>> (doppeUser: T) {
    return doppeDtoUserGetCreatorsMetaSiteId(doppeUser);
}

/**
 * Gets the Linked MetaSite ID for a Hopp project.
 * 
 * Returns the ID of an existing Wix site that the user has linked to their Hopp project
 * to showcase data from their existing site. Returns null if no site is linked.
 * 
 * Handles both onboarding and post-onboarding states:
 * - During onboarding: Returns intent from onboardingPreferences
 * - After onboarding: Returns actual linked MetaSite ID from wixMetaSiteId field
 * 
 * @param doppeUser - The Hopp project data
 * @returns Linked MetaSite ID (UUID) or null if no site is linked
 */
export function doppeDtoUserGetLinkedMetaSiteId (doppeUser: Pick<DoppeDtoUser, 'wixMetaSiteId' | 'creatorsWixMetaSiteId'> | DoppeDtoUser) {
    if ('ongoingOnboardingType' in doppeUser && doppeUser.ongoingOnboardingType !== null) {
        return doppeUser.onboardingPreferences?.wixMetaSiteId ?? null;
    }

    const metaSiteId = doppeUser.wixMetaSiteId;

    if (metaSiteId !== null && doppeUser.creatorsWixMetaSiteId !== metaSiteId) {
        return metaSiteId;
    }

    return null;
}

/**
 * Gets the MetaSite ID for a Hopp project based on the specified MetaSite type.
 * 
 * Acts as a dispatcher that abstracts the complexity of retrieving different types
 * of MetaSite IDs. Delegates to specialized helper functions based on the requested type.
 * 
 * @param doppeUser - The Hopp project data containing MetaSite information
 * @param metaSiteType - The type of MetaSite ID to retrieve
 * @returns The requested MetaSite ID (UUID string) or null if not available
 * 
 * @see {@link doppeDtoUserGetCreatorsMetaSiteId} - For Creators MetaSite logic
 * @see {@link doppeDtoUserGetLinkedMetaSiteId} - For Linked MetaSite logic
 */
export function doppeDtoUserGetMetaSiteIdByMetaSiteType (
    doppeUser: Pick<DoppeDtoUser, 'wixMetaSiteId' | 'creatorsWixMetaSiteId'> | DoppeDtoUser,
    metaSiteType: DoppeDtoUserMetaSiteType
) {
    switch (metaSiteType) {
        case DoppeContactsMetaSiteAutoValues.CreatorsMetaSite: {
            // Delegate to specialized function for Creators MetaSite logic
            return doppeDtoUserGetCreatorsMetaSiteId(doppeUser);
        }
        case DoppeContactsMetaSiteAutoValues.LinkedMetaSite: {
            // Delegate to specialized function for Linked MetaSite logic
            // Handles both onboarding and post-onboarding states
            return doppeDtoUserGetLinkedMetaSiteId(doppeUser);
        }
    }
}

export function doppeDtoUserResolveMediaMetaSiteId<T extends Pick<DoppeDtoUser, 'wixMetaSiteId'>> (
    doppeUser: T
) : T['wixMetaSiteId'] {
    return doppeUser.wixMetaSiteId;
}

export function doppeDtoUserGetFreemiumPlan (doppeUser: DoppeDtoUser.WithFreemiumPlanData) {
    return doppeDtoAppFeatureGetValue(doppeUser.userSettings, 'freemiumPlan');
}

export function doppeDtoUserIsPremiumModelEnabled (doppeUser: DoppeDtoUser.WithFreemiumPlanData) {
    return doppeFreemiumPlanIsEnabled(doppeDtoUserGetFreemiumPlan(doppeUser));
}

export function doppeDtoUserIsMultiplePackagesPremiumModel (doppeUser: DoppeDtoUser.WithFreemiumPlanData) {
    return doppeFreemiumPlanIsMultiplePackagesPremiumModel(doppeDtoUserGetFreemiumPlan(doppeUser))
}

export function doppeDtoUserIsSinglePackagePremiumModel (doppeUser: DoppeDtoUser.WithFreemiumPlanData) {
    return doppeFreemiumPlanIsSinglePackagePremiumModel(doppeDtoUserGetFreemiumPlan(doppeUser))
}


export function doppeDtoUserGetContactsMetaSite<T extends DoppeDtoUser> (doppeUser: T) {
    const userSettings = doppeUser.userSettings;

    return doppeDtoUserSettingsGetSettingValue(userSettings, 'userSelectedContactsMetaSite') ?? evaluateFunction(() => {
        if (doppeDtoUserSettingsGetSettingValue(userSettings, 'contactsMetaSiteSelectionWasEnabledWhenAccountCreated')) {
            return DoppeContactsMetaSiteAutoValues.CreatorsMetaSite
        } else {
            return doppeDtoUserGetLinkedMetaSiteId(doppeUser) !== null ?
                DoppeContactsMetaSiteAutoValues.LinkedMetaSite :
                DoppeContactsMetaSiteAutoValues.CreatorsMetaSite;
        }
    })
}

export function doppeDtoUserResolveContactsMetaSiteId<T extends DoppeDtoUser.Active> (doppeUser: T) {
    const contactsMetaSite = doppeDtoUserGetContactsMetaSite(doppeUser);

    switch (contactsMetaSite) {
        case DoppeContactsMetaSiteAutoValues.CreatorsMetaSite: {
            return assertNotNullable(doppeDtoUserGetCreatorsMetaSiteId(doppeUser));
        }
        case DoppeContactsMetaSiteAutoValues.LinkedMetaSite: {
            return assertNotNullable(doppeDtoUserGetLinkedMetaSiteId(doppeUser));
        }
        default: {
            return contactsMetaSite.metaSiteId;
        }
    }
}

export function doppeDtoUserExtendsUserSettingsWithDefaultValues<T extends DoppeDtoUser>(doppeUser: T) {
    return {
        ...doppeUser,
        userSettings: doppeDtoUserSettingsExtendDefaultValues(doppeUser.userSettings)
    } as T
}

export function doppeDtoUserGetAppVariant<T extends Pick<DoppeDtoUser, 'appVariant'>> (doppeUser: T) {
    return doppeUser.appVariant;
}

export function doppeDtoUserGetHtmlSiteUriMappings<T extends {userSettings: Pick<DoppeDtoUserSettings, 'htmlSiteUriMappings'>}> (
    doppeUser: T
) : DoppeHtmlSiteUriMappings {
    return doppeDtoUserSettingsGetSettingValue(doppeUser.userSettings, 'htmlSiteUriMappings');
}

export function doppeDtoUserGetHtmlSiteUriMappingsRootPaths<T extends {userSettings: Pick<DoppeDtoUserSettings, 'htmlSiteUriMappings'>}> (
    doppeUser: T
) : string[] {

    const htmlSiteUriMappings = doppeDtoUserGetHtmlSiteUriMappings(doppeUser);

    return iterableMapToArray(objectGetValues(htmlSiteUriMappings), (uriPath, skip) => {
        return valueConvertWhenNotNullable(uriPath, uriPath => arrayFirst(stringTrimPrefix(uriPath, '/').split('/'))) ?? skip;
    })
}


export function doppeDtoUserGetHtmlSiteUriMapping<T extends {userSettings: Pick<DoppeDtoUserSettings, 'htmlSiteUriMappings'>}> (
    doppeUser: T,
    htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType
) {
    return doppeDtoUserGetHtmlSiteUriMappings(doppeUser)[htmlSiteUriMappingType];
}